import React, {useEffect, useState} from 'react';
import {useQuery, useMutation} from '@apollo/client';
import {DELETE_AGENT, GET_AGENTS} from "../graphql/agents";
import {ListComponent} from "../components/ListComponent";
import people from "../assets/images/people-black.svg";
import {CardComponent} from "../components/CardComponent";
import {ListRowComponent} from "../components/ListRowComponent";
import {PersonCardContentComponent} from "../components/PersonCardContentComponent";
import {useNavigate} from 'react-router-dom';

const Agents = () => {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [agents, setAgents] = useState({});
    const [sortBy, setSortBy] = useState('lastName');
    const [viewMode, setViewMode] = useState('grid');

    const query = useQuery(GET_AGENTS,{
        variables: {
            pageIndex: 0,
            pageSize: 10,
            orderAsc: true,
            orderField: "lastName",
            filter: {}
        },
        onCompleted: (data) => {
            setAgents(data?.agents);
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            const {loading, error, data} = await query.refetch({
                    pageIndex: 0,
                    pageSize: 10,
                    orderAsc: true,
                    orderField: sortBy,
                    filter: { or: [{ firstName: { regex: searchTerm }}, {lastName: { regex: searchTerm }}, {email: { regex: searchTerm }}], isActive: true}
                }
            );
            setAgents(data?.agents);
        };

        const debounceTimer = setTimeout(() => {
            fetchData();
        }, 500); // 500ms delay

        return () => clearTimeout(debounceTimer);

    }, [searchTerm, sortBy, query]);

    const sortOptions = [
        {key: "lastName", value: "Sort by Last Name"},
        {key: "firstName", value: "Sort by First Name"},
        {key: "properties", value: "Sort by Properties Count"}
    ]

    const [deleteAgent] = useMutation(DELETE_AGENT, {
        refetchQueries: [{
            query: GET_AGENTS,
            variables: {
                pageIndex: 0,
                pageSize: 10,
                orderAsc: true,
                orderField: "lastName",
                filter: {}
            }
        }],
        onCompleted: () => {
            console.log('Agent deleted successfully');
        },
        onError: (error) => {
            console.error('Error deleting agent:', error);
            alert('Failed to delete agent. Please try again.');
        }
    });

    const handleDelete = (id, name) => {
        if (window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
            deleteAgent({variables: {id}});
        }
    };

    const handleDetails = (id) => {
        navigate(`/agents/${id}`);
    };

    const handleEdit = (id) => {
        navigate(`/agents/edit/${id}`);
    };

    if (query.loading) return (
        <div className="flex justify-center items-center h-64">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600"
                 role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    if (query.error) return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {query.error.message}</span>
        </div>
    );

    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('lastName');
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
    };

    return (<ListComponent
        title="Agents"
        headerIcon={people}
        addTitle="Agent"
        addLink="/agents/add"
        searchTerm={searchTerm}
        searchTermCallback={setSearchTerm}
        resetFiltersCallback={resetFilters}
        sortCallback={setSortBy}
        defaultSortOption={sortBy}
        sortOptions={sortOptions}
        viewMode={viewMode}
        viewModeCallback={handleViewModeChange}
        pageInfo={{
            totalCount: agents?.pageInfo?.totalCount,
            totalPages: agents?.pageInfo?.totalPages,
            pageSize: agents?.pageInfo?.pageSize,
            pageIndex: agents?.pageInfo?.pageIndex
        }}>

        {agents?.items?.map(agent => {
            const componentProps = {
                cardId: agent.id,
                deleteCallback: () => handleDelete(agent.id, `${agent.firstName} ${agent.lastName}`),
                editCallback: handleEdit,
                detailsCallback: handleDetails,
                headerImageText: `${agent.firstName.charAt(0)}${agent.lastName.charAt(0)}`,
                headerTitle: `${agent.firstName} ${agent.lastName}`,
                headerColor: 'bg-yellow-100 text-yellow-700',
                children: <PersonCardContentComponent 
                    email={agent.email}
                    phone={agent.primaryPhone}
                    secondaryPhone={agent.secondaryPhone}
                    properties={agent.properties}
                    additionalInfo={
                        <div className="flex flex-wrap gap-2 mt-2">
                            {agent.department && (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.department === 'rentals' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`}>
                                    {agent.department.charAt(0).toUpperCase() + agent.department.slice(1)}
                                </span>
                            )}
                            {agent.status && (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                                </span>
                            )}
                            {agent.pprRegistrationNumber && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    PPRA: {agent.pprRegistrationNumber}
                                </span>
                            )}
                        </div>
                    }
                />
            };

            return viewMode === 'list' ? (
                <ListRowComponent key={agent.id} {...componentProps} />
            ) : (
                <CardComponent key={agent.id} {...componentProps} />
            );
        })}
    </ListComponent>);
};

export default Agents;